import { menuConfig } from '@/config/menu.config';
import { PAGE_LOGIN } from '@/constants';
import { authService } from '@/service/auth.service';
import { useAppStore, useUserStore } from '@/store';
import { SUCCESS_CODE } from '@/types/api.config';
import type { User } from '@/types/user';
import { convertPermissions } from '@/utils/permission';
import {
  createContext,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

interface AuthContextType {
  loading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [loading, setLoading] = useState<boolean>(true);
  const { user, setUser } = useUserStore();

  const initialized = useRef(false);

  useEffect(() => {
    const initUser = async () => {
      let isAuthenticated = false;
      try {
        const response = await authService.me();
        const resultCode = response.header.resultCode;
        const permissions = convertPermissions(menuConfig);
        if (resultCode === SUCCESS_CODE) {
          const userData = response.result.content;
          if (!userData) {
            console.error('User data is null');
            return;
          }
          const user = {
            id: userData.userId,
            name: userData.displayName,
            email: userData.email,
            menuList: menuConfig,
            permissions: permissions,
          } as User;
          setUser(user);
          useAppStore.getState().initializeSessionSettings();
          isAuthenticated = true;
        } else {
          console.error(
            'Failed to fetch user info:',
            response.header.resultCode
          );
        }
      } catch (error) {
        console.error('Failed to initialize user state:', error);
      } finally {
        setLoading(false);
        if (!isAuthenticated) {
          window.location.href =
            PAGE_LOGIN + '?returnUrl=' + window.location.href;
        }
      }
    };
    if (user || initialized.current) {
      return;
    }

    initialized.current = true;
    initUser();
  }, [setUser, user]);

  // simplify auth check
  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider
      value={{
        loading,
        isAuthenticated,
      }}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
